import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { checkIsLazyLoadingMaster, claimDetailMigrateToClaimForm, findKeyValuePair, getIsPageNameCancel, getIsPageNameDetail,
 getIsPageNameEdit, getIsPageNameEditDischarge, getIsPageNameReconsider, getNextAllowStatus, getPayerDocs, migrateToUploadedDoc } from '../../modules/crm/inPatient/service/crmUtils';
import { RootState } from '../../store';
import { setClaimData , setIsArtemisClaim} from '../../store/actions/claimDetailsAction';
import { setDiagnosisFromPayerLookUp, setIsMinimize, setPayerDocs, setUploadedFiles,
  updatePackageSelection } from '../../store/actions/newAdmissionAction';
import { changePayer } from '../../store/actions/payerListAction';
import { PayerLookUpState } from '../../store/models/lookUpModel';
import { PayerInitialState } from '../../store/models/payerListModel';
import { Payer } from '../models/payer.model';
import { actions, OTHER_ID } from '../constants/crmConstants';
import useSetDefaultPayer from './useSetDefaultPayer';

export const useMigrateClaimDetail = (
  isDetailLoading: boolean,
  isLookUpLoading: boolean,
  originalClaimDetail: any,
  isOldClaimDetail: boolean = false,
): any => {
  const {
    allPayers: configuredPayers, payer,
  } = useSelector(({ payerList }: RootState): PayerInitialState => payerList);
  const { uniqueIdentifier: uniquePayerId, isOnMailPayer, isCorporatePayer, shortName } = payer || {};
  const payerLookUp = useSelector(({ payerLookUp: pLookUp }: RootState): PayerLookUpState => pLookUp) || {};
  const { isDeleteModal } = useSelector((state: RootState): any => state.admissionDetails);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { payerNotConfig } = useSelector((state: RootState): any => state.selectedInPatientDetail);
  const setDefaultPayer = useSetDefaultPayer();

  useEffect((): void => {
    if (!isEmpty(originalClaimDetail) && !isDetailLoading && !isLookUpLoading && isEmpty(payerLookUp[uniquePayerId])) {
      const { payerId: claimPayerId, productCode: claimProductCode } = originalClaimDetail;
      let payerDetail: any = {};

      if (claimPayerId && configuredPayers?.length) {
        const uniqueId = `${claimPayerId}${claimProductCode || ''}`;
        payerDetail = configuredPayers.find(({ uniqueIdentifier, productCode: pCode }: Payer): boolean =>
          uniqueIdentifier === uniqueId && (!claimProductCode || claimProductCode === pCode));
        dispatch(changePayer(payerDetail || setDefaultPayer));
      }
    }

    if (payerNotConfig && !isEmpty(originalClaimDetail)) {
      updateClaim(originalClaimDetail);
    }

    if (
      !isDetailLoading &&
      !isLookUpLoading &&
      !isEmpty(originalClaimDetail) &&
      (!isEmpty(payerLookUp[uniquePayerId]) || (isCorporatePayer || isOnMailPayer) || isOldClaimDetail)
    ) {
      if (getIsPageNameEdit(pathname) || getIsPageNameEditDischarge(pathname) || getIsPageNameReconsider(pathname)) {
        dispatch(setIsMinimize(false));
      }
      updateClaim(originalClaimDetail);
    }
  }, [isDetailLoading, isLookUpLoading, originalClaimDetail, payer]);

  const updateClaim = (claimDetail: any): void => {
    const { district = [], relation = [], designation = [] } = payerLookUp[uniquePayerId] || {};

    const { allowStatus, diagnosis: hdfcDiagnosis, procedureDesc: hdfcProcedure , isArtemis} = claimDetail;

    if (isArtemis) {
      dispatch(setIsArtemisClaim(isArtemis));
    }

    const [{ actionName } = { actionName: null }] = getNextAllowStatus(allowStatus, null, isDeleteModal ? actions.CANCEL : '');
    claimDetail['nextAllowStatus'] = actionName ? [actionName] : [];
    const { claimFlowData } = claimDetail;

    if (claimFlowData?.length) {
      const allDocuments = migrateToUploadedDoc(claimFlowData);
      const payerDocs = getPayerDocs(claimFlowData);
      dispatch(setUploadedFiles(allDocuments));
      dispatch(setPayerDocs(payerDocs));
    }
    claimDetail = claimDetailMigrateToClaimForm(claimDetail, pathname, configuredPayers, {
      district,
      relation,
      designation,
    });
    const { selectedPackage, automatedProcedure, diagnosis, procedure } = claimDetail;
    if (!getIsPageNameDetail(pathname) && !getIsPageNameCancel(pathname)) {
      if (automatedProcedure !== OTHER_ID) {
        const automationProceduresMappedValue =
          findKeyValuePair(payerLookUp[uniquePayerId]?.automationProcedures, 'id', automatedProcedure);
        claimDetail.automatedProcedure = automationProceduresMappedValue?.procedure;
      }
      if (diagnosis?.length > 0 && (shortName !== 'HDFC') && !checkIsLazyLoadingMaster('Diagnosis', payer?.lazyLoadMasterTypes || '')) {
        const resultArr: any = [];
        diagnosis?.map((ele: any): any => {
          const diagnosisMappedValue = findKeyValuePair((payerLookUp[uniquePayerId]?.diagnosis !== undefined
            ? payerLookUp[uniquePayerId]?.diagnosis : diagnosis), 'masterLookId', ele);
          if (diagnosisMappedValue !== undefined) {
            resultArr?.push(diagnosisMappedValue['payerMasterValue']);
          } else {
            resultArr.push(ele);
          }
        });
        claimDetail.diagnosis = resultArr;
      } else if (diagnosis?.length > 0 && ((shortName === 'HDFC' && process.env.REACT_APP_HDFC_FEATURES === 'true')
        || checkIsLazyLoadingMaster('Diagnosis', payer?.lazyLoadMasterTypes || ''))) {
          claimDetail.diagnosis = hdfcDiagnosis?.map((e: any): any => {
            e.name = e?.payerMasterValue;
            e.value = e?.payerMasterValue;
            dispatch(setDiagnosisFromPayerLookUp(e));  // setting from claim details
            return e?.payerMasterValue;  // Returning the payerMasterValue
          });
      }
      // during enhancement mapping the id of the Procedure
      if (procedure?.length > 0 && (shortName !== 'HDFC') && !checkIsLazyLoadingMaster('Procedure', payer?.lazyLoadMasterTypes || '')) {
        const procedureResultArr: any = [];
        procedure?.map((ele: any): any => {
          const procedureMappedValue = findKeyValuePair((payerLookUp[uniquePayerId]?.procedure !== undefined
            ? payerLookUp[uniquePayerId]?.procedure : procedure), 'masterLookId', ele);
          if (procedureMappedValue !== undefined) {
            procedureResultArr?.push(procedureMappedValue['payerMasterValue']);
          } else {
            procedureResultArr?.push(ele);
          }
        });
        claimDetail.procedure = procedureResultArr;
      } else if (procedure?.length > 0 && ((shortName === 'HDFC' && process.env.REACT_APP_HDFC_FEATURES === 'true')
        || checkIsLazyLoadingMaster('Procedure', payer?.lazyLoadMasterTypes || ''))) {
        claimDetail.procedure = hdfcProcedure?.map((e: any): any => e?.payerMasterValue);
      }
    }
    dispatch(updatePackageSelection(selectedPackage));
    dispatch(setClaimData(claimDetail));
  };
  return updateClaim;
};
